import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../../services/api";

export default function PayrollChecklistPanel({ darkMode, payMonth, onChanged, readOnly = false }) {
  const [checklists, setChecklists] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(null);

  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";

  const load = useCallback(async () => {
    if (!payMonth) return;
    setLoading(true);
    try {
      const [clRes, compRes] = await Promise.all([
        payrollOpsService.listChecklists(payMonth),
        payrollOpsService.getChecklistCompletions(payMonth),
      ]);
      setChecklists(Array.isArray(clRes?.data) ? clRes.data : []);
      setCompletions(Array.isArray(compRes?.data) ? compRes.data : []);
    } catch {
      setChecklists([]);
      setCompletions([]);
    } finally {
      setLoading(false);
    }
  }, [payMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const isDone = (checklistId, itemKey) =>
    completions.some((c) => c.checklistId === checklistId && c.itemKey === itemKey && c.completed);

  const items = useMemo(() => {
    const out = [];
    checklists.forEach((cl) => {
      (cl.itemsJson || []).forEach((item) => {
        out.push({ checklist: cl, item });
      });
    });
    return out;
  }, [checklists]);

  const doneCount = items.filter(({ checklist, item }) => isDone(checklist.id, item.key)).length;
  const requiredPending = items.filter(
    ({ checklist, item }) => item.required !== false && !isDone(checklist.id, item.key)
  ).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  const toggle = async (checklistId, itemKey, completed) => {
    if (readOnly) return;
    const key = `${checklistId}:${itemKey}`;
    setToggling(key);
    try {
      await payrollOpsService.upsertChecklistCompletion({
        checklistId,
        payMonth,
        itemKey,
        completed,
      });
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to update checklist");
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className={`flex h-28 items-center justify-center gap-2 rounded-xl border ${border}`}>
        <Loader2 className="animate-spin text-[hsl(var(--ds-primary))]" size={16} />
        <span className={`text-xs ${muted}`}>Loading checklist…</span>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className={`rounded-xl border px-4 py-5 text-center ${border}`}>
        <p className={`text-sm font-medium ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
          No checklist tasks due for {payMonth}
        </p>
        <p className={`mt-1 text-xs ${muted}`}>
          Add or schedule tasks under Payroll → Checklist Tasks. Only items matching this month’s
          frequency appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className={`rounded-xl border px-4 py-3 ${border} ${darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-surface))]"}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">
              {doneCount}/{items.length} complete
              {requiredPending > 0 && (
                <span className="ml-2 text-xs font-medium text-amber-700">
                  · {requiredPending} required open
                </span>
              )}
            </p>
            <p className={`text-[11px] ${muted}`}>Mark tasks done for this pay month</p>
          </div>
          {pct === 100 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-800">
              <CheckCircle2 size={12} /> All done
            </span>
          )}
        </div>
        <div className={`mt-2 h-1.5 overflow-hidden rounded-full ${darkMode ? "bg-[#333]" : "bg-[hsl(var(--ds-muted))]"}`}>
          <div
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className={`overflow-hidden rounded-xl border ${border}`}>
        <div className="max-h-72 overflow-auto">
          <table className="w-full text-xs">
            <thead className={`sticky top-0 ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}>
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Checklist</th>
                <th className="px-3 py-2.5 text-left font-semibold">Task</th>
                <th className="px-3 py-2.5 text-center font-semibold">Required</th>
                <th className="px-3 py-2.5 text-center font-semibold">Done</th>
              </tr>
            </thead>
            <tbody>
              {items.map(({ checklist, item }) => {
                const done = isDone(checklist.id, item.key);
                const key = `${checklist.id}:${item.key}`;
                return (
                  <tr
                    key={key}
                    className={`border-t ${darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"} ${
                      done ? (darkMode ? "bg-emerald-950/20" : "bg-emerald-50/40") : ""
                    }`}
                  >
                    <td className="px-3 py-2.5 align-top">
                      <div className="font-medium">{checklist.name}</div>
                      <div className={`text-[10px] capitalize ${muted}`}>
                        {(checklist.frequency || "monthly").replace(/_/g, " ")}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <div>{item.label}</div>
                      {item.linkPath && (
                        <a
                          href={item.linkPath}
                          className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium text-[hsl(var(--ds-primary))]"
                        >
                          {item.linkLabel || "Open module"} <ExternalLink size={10} />
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center align-top">
                      {item.required !== false ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                          Yes
                        </span>
                      ) : (
                        <span className={muted}>No</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center align-top">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
                        disabled={readOnly || toggling === key}
                        checked={done}
                        onChange={(e) => toggle(checklist.id, item.key, e.target.checked)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
